/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { EpisodeInfo, SeasonsData } from 'client-code/episode-guide';

import { Constants } from '../../core/constants';
import { DeviceModel } from '../../core/device';
import { BrandingUiInfoKeys } from '../../core/entities';
import { ContentType, InteractiveComponents } from '../../core/enums';
import { IInfoCard, InfoCardManager } from '../../core/infocard';
import { NdvrInteractor } from '../../core/interactors';
import { Locale, LocaleKeys } from '../../core/locale';
import { Log } from '../../core/logging';
import { ScreenTaskControllerTypes } from '../../core/navigation';
import { ScreenController } from '../../core/navigation/ScreenController';
import { NetworkManager } from '../../core/network/NetworkManager';
import { SharedPrefConstants, SharedPreferences } from '../../core/storage';
import type { AppDispatch, TGetState, TivoPayloadAction } from '../../core/tivo/tivo-redux-wrapper';
import { TListViewType, TVodPlayActionStateType } from '../../core/types';
import { ListViewInfoCardUserInputHandler } from '../../core/usecases';
import { BrandingUiUtility, ListTypes } from '../../core/utils';
import { CcbSwitch } from '../../core/utils/ccb';
import { CcbSwitchName } from '../../core/utils/ccb/enums';
import { AllEpisodesListUtility } from '../../views/ListViewScreen/AllEpisodesList/AllEpisodesListUtility';
import { WhisperTypes } from '../../views/Whisper/WhisperTypes';
import {
    handleAllEpisodesGhostModeForPicker,
    onKeyBackAllepisodesList,
    onKeyBackOnAllEpisodesListGoToOverlay,
    onKeyDownAllepisodesList,
    onKeyLeftAllepisodesList,
    onKeyOKAllepisodesList,
    onKeyOkOnAllEpisodesListGoToOverlay,
    onKeyRightAllepisodesList,
    onKeyUpAllepisodesList,
    onMouseClickAllEpisodesList,
    onMouseClickOnAllEpisodesListGoToOverlay,
    onMouseOutAllEpisodesList,
    onMouseOverAllEpisodesList,
} from '../allepisodeslist/allepisodeslist.reducers';
import {
    clearActiveInteractiveComponent,
    setActiveInteractiveComponent,
} from '../interactivelayer/interactiveLayer.reducers';
import { setListEntryPosition } from '../list/list.actions';
import { onKeyDownList, onKeyUpList, onMouseOutList, onMouseOverList } from '../list/list.reducers';
import {
    onKeyLeftOnePassOptionsOverlay,
    onKeyOkDOCOverlay,
    onKeyOkOnePassOptionsOverlay,
    onKeyRightOnePassOptionsOverlay,
    onMouseClickDOCOptionsOverlay,
    onMouseClickOnePassOptionsOverlay,
} from '../overlay/onepassoptionsoverlay/onepassoptionsoverlay.reducers';
import {
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
    showPCPinOverlayWithProgramRating,
} from '../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    onKeyLeftRecordingOverlay,
    onKeyOkRecordingOverlay,
    onKeyOkStopOrCancelRecording,
    onKeyRightRecordingOverlay,
    onMouseClickRecordingOverlay,
    onMouseClickStopOrCancelRecording,
} from '../overlay/recordingoverlay/recordingoverlay.reducers';
import {
    onKeyOkStorageLimitOverlay,
    onMouseClickStorageLimitOverlay,
} from '../overlay/storagelimitoverlay/storagelimitoverlay.reducers';
import {
    onKeyOkVodPlayErrorOverlay,
    onMouseClickVodPlayErrorOverlay,
} from '../overlay/vodplayerroroverlay/vodplayerroroverlay.reducers';
import {
    onKeyOkVodUpSellOverlay,
    onMouseClickVodUpSellOverlay,
} from '../overlay/vodupselloverlay/vodupselloverlay.reducers';
import {
    hideOverlayDialog,
    onKeyDownOverlayDialog,
    onKeyUpOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../overlaydialog/overlaydialog.reducers';
import { programScreenTransition } from '../screens/navigation/screensnavigation';
import { showPreviousScreen } from '../screens/screens.reducers';
import { notifyVodPlayActionState } from '../vodplayaction/vodplayaction.reducers';
import { showWhisper } from '../whisper/whisper.reducer';
import {
    setListEntryActions,
    setListEntryPageActions,
    setListViewScreenGhostModeAction,
    updateListScreenCaretVisibilityAction,
} from './listviewscreen.actions';
import {
    LIST_SCREEN_GHOST_HIGHLIGHT,
    LIST_SCREEN_LIMIT,
    LIST_SCREEN_UPDATE_CARET_VISIBLE,
    SET_LIST_ENTRY_PAGE,
    SET_VIEW_SCREEN_LIST_ENTRY_POSITION,
} from './listviewscreen.types';
import {
    getOnDemandOffsetFromPage,
    getOnDemandPage,
    getOnDemandPageIndex,
} from './ListviewScreenUtil';

const TAG = 'ListViewScreenReducer';

interface IListViewScreen {
    position: number;
    limit: number;
    page: number;
    isCaretVisible: boolean;
    ghostMode: boolean;
}
const initialState: IListViewScreen = {
    position: 0,
    limit: 18, // Default limit for the number of entries displayed in the list view
    page: 0, // Default page number for pagination
    isCaretVisible: false,
    ghostMode: false,
};

const MAX_ITEM_IN_PAGE = 18;
const MAX_ITEM_IN_PAGE_ON_DEMAND = 19;

const listViewScreenReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IListViewScreen>>{},
) => {
    switch (type) {
        case SET_VIEW_SCREEN_LIST_ENTRY_POSITION:
            return { ...state, position: payload.position };
        case SET_LIST_ENTRY_PAGE:
            return { ...state, page: payload.page };
        case LIST_SCREEN_GHOST_HIGHLIGHT:
            return { ...state, ghostMode: payload.ghostMode };
        case LIST_SCREEN_UPDATE_CARET_VISIBLE:
            return { ...state, isCaretVisible: payload.isCaretVisible };
        case LIST_SCREEN_LIMIT:
            return { ...state, limit: payload.limit };
        default:
            return state;
    }
};
const doAdultParentalControlCheck =
    (isAdult: boolean, callBack: Function, ...callBackParams: any[]) =>
    (dispatch: Function) => {
        const services = new NdvrInteractor();
        if (isAdult && services.shouldHideMetadata()) {
            dispatch(
                showPCPinOverlayWithProgramRating(() => {
                    dispatch(callBack(...callBackParams));
                }, isAdult),
            );
            return;
        }
        dispatch(callBack(...callBackParams, showWhisper));
    };

const overlayKeyHandler =
    (screenTaskController: string, callback: Function) => (dispatch: AppDispatch) => {
        const overlayTaskController: string[] = [
            ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS,
            ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS,
            ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS,
            ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK,
            ScreenTaskControllerTypes.SLO_TASKS,
            ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS,
            ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS,
            ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS,
            ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY,
            ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS,
            ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY,
            ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS,
            ScreenTaskControllerTypes.GO_TO_OVERLAY_TASKS,
        ];

        if (overlayTaskController.includes(screenTaskController)) {
            dispatch(callback());
        }
    };

const getSeasonDataParamsForListType = (
    listType: TListViewType,
    id: string,
    partnerId?: string,
    collapsedVodItemId?: string,
): any => {
    const filter = listType === 'onDemand' ? 'vodOnDemand' : 'all';
    const getSeasonDataParams: any = {
        unifiedCollectionId: id,
        includeStickyInfo: true,
        filter,
    };

    if (listType === 'onDemand') {
        getSeasonDataParams.partnerId = partnerId || '';
        getSeasonDataParams.collapsedVodItemId = collapsedVodItemId || '';
    }

    return getSeasonDataParams;
};

export const onKeyDownListView = () => (dispatch: Function, getState: Function) => {
    const { listViewScreen } = getState();
    const { position, ghostMode, page, limit } = listViewScreen;
    const listPosition = getState().list[ListTypes.ON_DEMAND_LIST]?.position ?? 0;

    if (ghostMode) {
        dispatch(handleAllEpisodesGhostModeForPicker());
        dispatch(setListViewScreenGhostModeAction(false));
        return;
    }

    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDownPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.GO_TO_OVERLAY_TASKS:
            dispatch(onKeyDownOverlayDialog());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            {
                const onDemandSharedPref = new SharedPreferences(
                    SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;
                const totalCount = new SharedPreferences(
                    SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                ).getNumber(SharedPrefConstants.LIST_SCREEN_TOTAL_COUNT);

                if (onDemandSharedPref.type === 'onDemand') {
                    if (page * limit + position >= totalCount - 1) return;
                    const isHeader = onDemandSharedPref.flattenedList[listPosition + 1]?.isHeader;
                    if (isHeader) {
                        const currentPosition =
                            getState().list[ListTypes.ON_DEMAND_LIST]?.position ?? 0;
                        dispatch(
                            setListEntryPosition(
                                ListTypes.ON_DEMAND_LIST,
                                currentPosition + 1 || 0,
                            ),
                        );
                    }
                    if (listPosition === limit && totalCount > (page + 1) * limit) {
                        const listSharedPreferenceEditor = DeviceModel.getSharedPreferences(
                            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                        ).edit();
                        listSharedPreferenceEditor.putNumber(
                            SharedPrefConstants.LISTVIEW_HIGHLIGHT_INDEX,
                            0,
                        );
                        listSharedPreferenceEditor.putNumber(
                            SharedPrefConstants.LISTVIEW_PREVIOUS_HIGHLIGHT_INDEX,
                            limit,
                        );
                        listSharedPreferenceEditor.apply();
                        dispatch(setListEntryPageActions(page + 1));
                        dispatch(setListEntryActions(0));
                    }
                    dispatch(onKeyDownList(ListTypes.ON_DEMAND_LIST));
                } else if (onDemandSharedPref.type === 'allEpisodes') {
                    dispatch(
                        onKeyDownAllepisodesList(
                            onDemandSharedPref.flattenedList,
                            page,
                            totalCount,
                            onDemandSharedPref.id,
                        ),
                    );
                } else {
                    if (page * limit + position >= totalCount - 1) {
                        return; // Prevent going beyond the last entry
                    }
                    if (position === limit - 1 && totalCount > (page + 1) * limit) {
                        dispatch(setListEntryPageActions(page + 1));
                    }
                    dispatch(setListEntryActions((position + 1) % limit));
                }
            }
            break;
        default:
            dispatch(overlayKeyHandler(screenTaskController, onKeyDownOverlayDialog));
    }
};

export const onKeyUpListView = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { listViewScreen } = getState();
    const { position, ghostMode, page, limit } = listViewScreen;
    const listPosition = getState().list[ListTypes.ON_DEMAND_LIST]?.position ?? 0;

    if (ghostMode) {
        dispatch(handleAllEpisodesGhostModeForPicker());
        dispatch(setListViewScreenGhostModeAction(false));
        return;
    }

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyUpPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.GO_TO_OVERLAY_TASKS:
            dispatch(onKeyUpOverlayDialog());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            {
                const onDemandSharedPref = new SharedPreferences(
                    SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;
                if (onDemandSharedPref.type === 'onDemand') {
                    const isHeader = onDemandSharedPref.flattenedList[listPosition - 1]?.isHeader;
                    if (listPosition === 1 && isHeader) return;
                    if (isHeader) {
                        const currentPosition =
                            getState().list[ListTypes.ON_DEMAND_LIST]?.position ?? 0;
                        dispatch(
                            setListEntryPosition(
                                ListTypes.ON_DEMAND_LIST,
                                currentPosition - 1 || 0,
                            ),
                        );
                    }
                    if (page && listPosition === 0) {
                        dispatch(setListEntryPageActions(page - 1));
                        const previousPageHighlightIndex = new SharedPreferences(
                            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                        ).getNumber(SharedPrefConstants.LISTVIEW_PREVIOUS_HIGHLIGHT_INDEX);
                        const listSharedPreferenceEditor = DeviceModel.getSharedPreferences(
                            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                        ).edit();
                        listSharedPreferenceEditor.putNumber(
                            SharedPrefConstants.LISTVIEW_HIGHLIGHT_INDEX,
                            previousPageHighlightIndex,
                        );
                        listSharedPreferenceEditor.apply();
                        dispatch(setListEntryPosition(ListTypes.ON_DEMAND_LIST, limit));
                        return;
                    }
                    dispatch(onKeyUpList(ListTypes.ON_DEMAND_LIST));
                } else if (onDemandSharedPref.type === 'allEpisodes') {
                    dispatch(
                        onKeyUpAllepisodesList(
                            onDemandSharedPref.flattenedList,
                            page,
                            onDemandSharedPref.id,
                        ),
                    );
                } else {
                    if (!page && position <= 0) {
                        return; // Prevent going above the first entry
                    }
                    if (page && position === 0) {
                        dispatch(setListEntryPageActions(page - 1));
                    }
                    dispatch(setListEntryActions((position - 1 + limit) % limit));
                }
            }
            break;
        default:
            dispatch(overlayKeyHandler(screenTaskController, onKeyUpOverlayDialog));
    }
};

export const onKeyOkListView = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onKeyOkVodPlayErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onKeyOkVodUpSellOverlay());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyOkPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkDOCOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyOkOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.SLO_TASKS:
            dispatch(onKeyOkStorageLimitOverlay());
            break;
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
            dispatch(onKeyOkStopOrCancelRecording());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyOkRecordingOverlay());
            break;
        case ScreenTaskControllerTypes.GO_TO_OVERLAY_TASKS:
            dispatch(onKeyOkOnAllEpisodesListGoToOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            {
                if (NetworkManager.isNetworkDisconnected()) {
                    const service = new NdvrInteractor();
                    const message = BrandingUiUtility.formatDescription(
                        Locale.getString(LocaleKeys.NO_NETWORK_WHISPER_DESCRIPTION),
                        {
                            DEVICE_NAME_SHORT: service.getBrandingUiInfoForKey(
                                BrandingUiInfoKeys.DEVICE_NAME_SHORT,
                            ),
                            BUTTON_NAME_SELECT: DeviceModel.isWebOsPlatform()
                                ? Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_LG)
                                : Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_SAMSUNG),
                        },
                    );
                    dispatch(showWhisper({ message, iconType: WhisperTypes.INFO }));
                    return;
                }
                // Save current position to screen properties for back stickiness
                let listItemInfo = new SharedPreferences(
                    SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;

                if (listItemInfo.type === 'onDemand') {
                    const currentPosition =
                        getState().list[ListTypes.ON_DEMAND_LIST]?.position ?? 0;
                    const listSharedPreferenceEditor = DeviceModel.getSharedPreferences(
                        SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                    ).edit();
                    listSharedPreferenceEditor.putNumber(
                        SharedPrefConstants.LISTVIEW_HIGHLIGHT_INDEX,
                        currentPosition,
                    );
                    listSharedPreferenceEditor.apply();
                    const currScreen = ScreenController.getInstance().getScreen();
                    const currScreenProperties = currScreen.getProperties() || {};
                    currScreen.setProperties({
                        ...currScreenProperties,
                        highlight: currentPosition,
                    });
                    if (listItemInfo.flattenedList && listItemInfo.flattenedList.length === 0)
                        return;
                    listItemInfo = listItemInfo.flattenedList[currentPosition];
                    dispatch(
                        doAdultParentalControlCheck(
                            listItemInfo?.vodItemKernel?.isAdult,
                            programScreenTransition,
                            {
                                id: listItemInfo.contentId || '',
                                contentType: ContentType.CONTENT_ID,
                                partnerId: (currScreenProperties as any).partnerId || '',
                                collapsedVodItemId:
                                    (currScreenProperties as any).collapsedVodItemId || '',
                            },
                        ),
                    );
                } else if (listItemInfo.type === 'allEpisodes') {
                    dispatch(onKeyOKAllepisodesList(listItemInfo.flattenedList, listItemInfo.id));
                } else {
                    dispatch(
                        doAdultParentalControlCheck(
                            listItemInfo?.vodItemKernel?.isAdult,
                            programScreenTransition,
                            {
                                id:
                                    listItemInfo.type === 'upcoming'
                                        ? listItemInfo.offerId
                                        : listItemInfo?.vodItemKernel?.offerId,
                                contentType:
                                    listItemInfo.type === 'upcoming'
                                        ? ContentType.OFFER_ID
                                        : ContentType.VOD_OFFER_ID,
                            },
                        ),
                    );
                }
            }
            break;
        default:
    }
};

export const onKeyPlayListView = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK) {
        return;
    }
    const listItemInfo = new SharedPreferences(SharedPrefConstants.LIST_SCREEN_PREF_KEY).getJson(
        SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION,
    ) as any;
    if (!listItemInfo) {
        return;
    }
    // Block play for allEpisodes list
    if (listItemInfo.type === 'allEpisodes') {
        return;
    }
    dispatch(
        notifyVodPlayActionState({
            type: TVodPlayActionStateType.INIT,
            vodRequiredParams: {
                id: listItemInfo?.vodItemKernel?.offerId,
                itemType: 'offer',
                partnerId: listItemInfo?.vodItemKernel?.videoProviderId,
            },
        }),
    );
};

export const onKeyLeftListView = () => (dispatch: Function, getState: Function) => {
    const { ghostMode } = getState().listViewScreen;

    if (ghostMode) {
        dispatch(handleAllEpisodesGhostModeForPicker());
        dispatch(setListViewScreenGhostModeAction(false));
        return;
    }

    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyLeftPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyLeftOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyLeftRecordingOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            {
                const listItemInfo = new SharedPreferences(
                    SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;

                if (listItemInfo.type === 'allEpisodes') {
                    dispatch(onKeyLeftAllepisodesList(listItemInfo.flattenedList));
                }
            }
            break;
        default:
    }
};

export const onKeyRightListView = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { ghostMode } = getState().listViewScreen;

    if (ghostMode) {
        dispatch(handleAllEpisodesGhostModeForPicker());
        dispatch(setListViewScreenGhostModeAction(false));
        return;
    }

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyRightPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyRightOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyRightRecordingOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            {
                const listItemInfo = new SharedPreferences(
                    SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;

                if (listItemInfo.type === 'allEpisodes') {
                    dispatch(onKeyRightAllepisodesList(listItemInfo.flattenedList));
                }
            }
            break;
        default:
    }
};

export const onKeyDigitListView = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDigitPcPinOverlay());
            break;
        default:
    }
};

export const onKeyBackListView = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyBackPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.GO_TO_OVERLAY_TASKS:
            {
                const listItemInfo = new SharedPreferences(
                    SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;

                if (listItemInfo?.type === 'allEpisodes') {
                    dispatch(onKeyBackOnAllEpisodesListGoToOverlay());
                } else {
                    dispatch(hideOverlayDialog());
                }
            }
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            dispatch(saveListViewScreenSticknessInfo());
            {
                const listItemInfo = new SharedPreferences(
                    SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;

                if (listItemInfo?.type === 'allEpisodes') {
                    dispatch(onKeyBackAllepisodesList(listItemInfo.flattenedList, listItemInfo.id));
                }
                dispatch(showPreviousScreen());
            }
            break;
        default:
            dispatch(overlayKeyHandler(screenTaskController, hideOverlayDialog));
    }
};

export const onKeyInfoListView = () => async (dispatch: AppDispatch, getState: TGetState) => {
    if (
        !CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_INFO_CARD_REMOVE_IN_KST_12193,
        )
    )
        return;
    const { screenTaskController } = getState().screens;
    if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK) return;
    const { id, listType, partnerId, collapsedVodItemId } = (ScreenController.getInstance()
        .getScreen()
        .getProperties() || {}) as {
        id: string;
        listType: TListViewType;
        partnerId?: string;
        collapsedVodItemId?: string;
    };

    const { page, position } = getState().listViewScreen;
    const listItemInfo = new SharedPreferences(SharedPrefConstants.LIST_SCREEN_PREF_KEY);
    const totalItemCount = listItemInfo.getNumber(SharedPrefConstants.LIST_SCREEN_TOTAL_COUNT);
    let initialPageList = listItemInfo.getJson('list') as unknown as any[];
    let currentItem = listItemInfo.getJson(
        SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION,
    ) as any;
    let maxItemsPerPage = MAX_ITEM_IN_PAGE;
    if (listType === 'onDemand') {
        maxItemsPerPage = MAX_ITEM_IN_PAGE_ON_DEMAND;
    } else if (listType === 'allEpisodes') {
        maxItemsPerPage = AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES;
    }
    let maxInPage = Math.min(maxItemsPerPage, totalItemCount);
    let initialPageOffset = page * maxInPage;
    let activeItemIndex = page * maxInPage + position;

    // Returning if no current item
    if (!currentItem) {
        return;
    }

    // Need to do this as structure of onDemand list is different
    // Also removing the season related data so that we don't have any specific logic in info card
    if (listType === 'onDemand' || listType === 'allEpisodes') {
        const listTypeKey =
            listType === 'allEpisodes' ? ListTypes.ALL_EPISODES_LIST : ListTypes.ON_DEMAND_LIST;
        const currentPosition = getState().list[listTypeKey]?.position ?? 0;
        const flattenedList: EpisodeInfo[] = currentItem.flattenedList || [];
        currentItem = flattenedList[currentPosition];
        initialPageList = flattenedList.filter((i) => i.contentId); // Remove season headers

        maxInPage = initialPageList.length;
        activeItemIndex = flattenedList.slice(0, currentPosition).filter((i) => i.contentId).length;

        const getSeasonDataParams = getSeasonDataParamsForListType(
            listType,
            id,
            partnerId,
            collapsedVodItemId,
        );
        const seasonResponse: SeasonsData = await new NdvrInteractor().getSeasonData(
            getSeasonDataParams,
        );

        // Update offset if season picker is present
        if ((seasonResponse.seasons || []).length > 1) {
            initialPageOffset =
                listType === 'allEpisodes'
                    ? AllEpisodesListUtility.getAllEpisodesOffsetFromPage(
                          page,
                          seasonResponse.seasons || [],
                      )
                    : getOnDemandOffsetFromPage(page, seasonResponse.seasons || []);
            activeItemIndex += initialPageOffset;
            if (initialPageOffset === -1) {
                Log.e(TAG, 'Error while processing info card for onDemand list view');
                return;
            }
        }
    }

    const infoCard: IInfoCard = InfoCardManager.getInstance();
    const listViewInfoCardUserInputHandler = new ListViewInfoCardUserInputHandler();

    infoCard.addUserInputEventHandler(listViewInfoCardUserInputHandler);
    infoCard.setProperties({
        hasItemsBefore: activeItemIndex > 0,
        hasItemsAfter: activeItemIndex < totalItemCount - 1,
    });
    infoCard.setContextData(ListViewInfoCardUserInputHandler.name, {
        activeItemIndex,
        initialPageOffset,
        totalItemCount,
        initialPageList,
        listType,
        maxInPage,
        id,
        partnerId,
        collapsedVodItemId,
    });
    infoCard.setInfoCardKernel(
        listViewInfoCardUserInputHandler.getInfoCardKernel(currentItem, listType, {
            partnerId,
            collapsedVodItemId,
        }),
    );
    infoCard.show();
};

export const onInfoCardDismissListView = () => async (dispatch: AppDispatch) => {
    const infoCard = InfoCardManager.getInstance();
    const contextData = infoCard.getContextData(ListViewInfoCardUserInputHandler.name) || {};
    const {
        activeItemIndex,
        initialPageOffset,
        maxInPage,
        id,
        listType,
        partnerId,
        collapsedVodItemId,
    } = contextData;

    if (listType === 'onDemand' || listType === 'allEpisodes') {
        const getSeasonDataParams = getSeasonDataParamsForListType(
            listType,
            id,
            partnerId,
            collapsedVodItemId,
        );
        const seasonResponse: SeasonsData = await new NdvrInteractor().getSeasonData(
            getSeasonDataParams,
        );

        const maxItemsPerPage =
            listType === 'onDemand'
                ? MAX_ITEM_IN_PAGE_ON_DEMAND
                : AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES;
        let pageNo = Math.floor(activeItemIndex / maxItemsPerPage);
        let pageIndex = activeItemIndex % maxItemsPerPage;

        // Update if season picker is present
        if ((seasonResponse.seasons || []).length > 1) {
            const { page: p, pageSpaceIndex } =
                listType === 'allEpisodes'
                    ? AllEpisodesListUtility.getAllEpisodesPage(
                          activeItemIndex,
                          seasonResponse.seasons || [],
                      )
                    : getOnDemandPage(activeItemIndex, seasonResponse.seasons || []);
            pageNo = p;
            pageIndex =
                listType === 'allEpisodes'
                    ? AllEpisodesListUtility.getAllEpisodesPageIndex(
                          p,
                          pageSpaceIndex,
                          seasonResponse.seasons || [],
                      )
                    : getOnDemandPageIndex(p, pageSpaceIndex, seasonResponse.seasons || []);
        }

        dispatch(setListEntryPageActions(pageNo));
        const targetListType =
            listType === 'allEpisodes' ? ListTypes.ALL_EPISODES_LIST : ListTypes.ON_DEMAND_LIST;
        dispatch(setListEntryPosition(targetListType, pageIndex));
        return;
    }

    const itemExistInPage =
        activeItemIndex >= initialPageOffset &&
        activeItemIndex <= initialPageOffset + maxInPage - 1;
    if (itemExistInPage) {
        dispatch(setListEntryActions(activeItemIndex - initialPageOffset));
    } else {
        dispatch(setListEntryPageActions(Math.floor(activeItemIndex / maxInPage)));
        dispatch(setListEntryActions(activeItemIndex % maxInPage));
    }
};

export const onLongKeyOkListView = () => async (dispatch: AppDispatch) => {
    dispatch(onKeyInfoListView());
};

export const onMouseOkLongPressStartListView = () => (dispatch: AppDispatch) => {
    dispatch(onLongKeyOkListView());
};

export const listScreenSetCaretInvisible = () => (dispatch: Function) => {
    dispatch(clearActiveInteractiveComponent());
    dispatch(updateListScreenCaretVisibilityAction(false));
};

export const listScreenSetCaretVisible = () => (dispatch: Function) => {
    dispatch(updateListScreenCaretVisibilityAction(true));
};

export const onMouseOverListView = () => (dispatch: Function, getState: Function) => {
    const {
        inputFactory: { value },
        screens: { screenTaskController },
        listViewScreen: { page, limit },
    } = getState();

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            dispatch(handleDefaultListScreenMouseOver(value, page, limit));
            break;
        default:
            dispatch(overlayKeyHandler(screenTaskController, onMouseOverOverlayDialogOperations));
    }
};

const handleDefaultListScreenMouseOver =
    (value: string, page: number, limit: number) => (dispatch: Function) => {
        const onDemandSharedPref = new SharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;

        const totalCount = new SharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        ).getNumber(SharedPrefConstants.LIST_SCREEN_TOTAL_COUNT);

        const effectiveLimit =
            onDemandSharedPref?.type === 'allEpisodes'
                ? AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES
                : limit;

        if (value === Constants.VIDEO_PLAYER_UI_ID) return;

        if (value === InteractiveComponents.NORTH && page) {
            dispatch(listScreenSetCaretVisible());
            dispatch(setActiveInteractiveComponent(value));
            return;
        }

        if (value === InteractiveComponents.SOUTH && (page + 1) * effectiveLimit < totalCount) {
            dispatch(listScreenSetCaretVisible());
            dispatch(setActiveInteractiveComponent(value));
            return;
        }
        if (onDemandSharedPref.type === 'onDemand') {
            dispatch(onMouseOverList());
        } else if (onDemandSharedPref.type === 'allEpisodes') {
            dispatch(onMouseOverAllEpisodesList(value, onDemandSharedPref.flattenedList));
        } else {
            // Handle regular list item mouse over
            dispatch(setListViewScreenGhostModeAction(false));
            const position = value.split('_')[0];
            dispatch(setListEntryActions(Number(position) || 0));
        }
    };

export const onMouseOutListView = () => (dispatch: Function, getState: Function) => {
    const {
        inputFactory: { value },
        screens: { screenTaskController },
    } = getState();

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            dispatch(handleDefaultListScreenMouseOut(value));
            break;
        default:
            dispatch(overlayKeyHandler(screenTaskController, onMouseOutOverlayDialogOperations));
    }
};

const handleDefaultListScreenMouseOut = (value: string) => (dispatch: Function) => {
    const onDemandSharedPref = new SharedPreferences(
        SharedPrefConstants.LIST_SCREEN_PREF_KEY,
    ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;

    dispatch(setListViewScreenGhostModeAction(true));

    if (value === InteractiveComponents.NORTH || value === InteractiveComponents.SOUTH) {
        dispatch(clearActiveInteractiveComponent());
        dispatch(listScreenSetCaretInvisible());
    }

    if (onDemandSharedPref.type === 'onDemand') {
        dispatch(onMouseOutList());
    } else if (onDemandSharedPref.type === 'allEpisodes') {
        dispatch(onMouseOutAllEpisodesList(value));
    }
};

const handleDefaultListScreenMouseClick =
    (value: string, page: number, limit: number) => (dispatch: Function) => {
        const totalCount = new SharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        ).getNumber(SharedPrefConstants.LIST_SCREEN_TOTAL_COUNT);

        const onDemandSharedPref = new SharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;

        const effectiveLimit =
            onDemandSharedPref?.type === 'allEpisodes'
                ? AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES
                : limit;

        switch (value) {
            case InteractiveComponents.NORTH:
                if (page) {
                    dispatch(setListEntryPageActions(page - 1));
                }
                return;
            case InteractiveComponents.SOUTH:
                if ((page + 1) * effectiveLimit < totalCount) {
                    const newPage = page + 1;
                    dispatch(setListEntryPageActions(newPage));
                    // Hide the SOUTH caret if we've reached the last page
                    if ((newPage + 1) * effectiveLimit >= totalCount) {
                        dispatch(listScreenSetCaretInvisible());
                    }
                }
                return;
            default:
        }

        // Handle list type specific clicks
        if (onDemandSharedPref.type === 'allEpisodes' && value.includes('season-picker')) {
            dispatch(onMouseClickAllEpisodesList(value));
            return;
        }

        dispatch(onKeyOkListView());
    };

export const onMouseClickListView = () => (dispatch: Function, getState: Function) => {
    const {
        inputFactory: { value },
        screens: { screenTaskController },
        listViewScreen: { page, limit },
    } = getState();

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onMouseClickVodPlayErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseClickVodUpSellOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            if (value === Constants.VIDEO_PLAYER_UI_ID) return;
            dispatch(handleDefaultListScreenMouseClick(value, page, limit));
            break;
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onMouseClickDOCOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onMouseClickOnePassOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.SLO_TASKS:
            dispatch(onMouseClickStorageLimitOverlay());
            break;
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
            dispatch(onMouseClickStopOrCancelRecording());
            break;
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onMouseClickRecordingOverlay());
            break;
        case ScreenTaskControllerTypes.GO_TO_OVERLAY_TASKS:
            dispatch(onMouseClickOnAllEpisodesListGoToOverlay());
            break;
        default:
    }
};

export const onMouseWheelListView =
    (wheelDeltaY: number) => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;

        if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
            return; // no action needed
        }

        if (wheelDeltaY < 0) dispatch(onKeyDownListView());
        if (wheelDeltaY > 0) dispatch(onKeyUpListView());
    };

const saveListViewScreenSticknessInfo = () => (dispatch: AppDispatch, getState: TGetState) => {
    const currentPosition = getState().list[ListTypes.ON_DEMAND_LIST]?.position || 0;
    const currScreen = ScreenController.getInstance().getScreen();
    const currScreenProperties = ScreenController.getInstance().getScreen().getProperties()!;
    currScreen.setProperties({
        ...currScreenProperties,
        horizontalIndex: currentPosition,
    });

    const listSharedPreferenceEditor = DeviceModel.getSharedPreferences(
        SharedPrefConstants.LIST_SCREEN_PREF_KEY,
    ).edit();
    listSharedPreferenceEditor.putNumber(
        SharedPrefConstants.LISTVIEW_HIGHLIGHT_INDEX,
        currentPosition,
    );
    listSharedPreferenceEditor.apply();
};

export default listViewScreenReducer;
